import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { ApiError } from "@/lib/api/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useSubmitProfile } from "@/features/profile/hooks";

/**
 * Sending a sent-back profile to HR again.
 *
 * Saving a correction does not send anything: the employee says when they
 * have finished. In November that was the whole problem. An employee HR had
 * sent back corrected eleven fields one morning and sent nothing until the
 * next day, because the only way back to HR was the button at the foot of the
 * last page, and HR saw a profile nobody had touched. So the button is on
 * every page of the form while the profile is with them, and on the page they
 * land on.
 */
export function useSendBack() {
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const submit = useSubmitProfile();
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setError(null);
    try {
      await submit.mutateAsync(undefined);
      await refreshSession();
      toast.success("Sent back to HR.", { description: "HR will look at your changes." });
      navigate("/pending", { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not send it. Check your connection and try again.",
      );
    }
  }

  return { send, sending: submit.isPending, error };
}

/** Whether the profile is with the employee because HR asked for changes. */
export function useSentBack(): boolean {
  const { session } = useAuth();
  return session?.user.status === "rejected";
}
